/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLayout } from '@layout/app/LayoutContext';
import { SideMenuLink } from '@layout/app/side-menu/SideMenuLink';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@features/agency/hooks/use-agency-permissions';
import type { CalendarEventInput } from '@services/requests/housien-models/CalendarEventInput';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { viewRange } from '@features/agenda/utils/agenda-dates';
import { canManageEvent } from '@features/agenda/utils/agenda-permissions';
import { canWriteToCalendar, writableCalendars } from '@features/agenda/utils/calendar-write-access';
import { otherMembers, memberColors } from '@features/agenda/utils/agenda-grouping';
import { expandEvents } from '@features/agenda/utils/expand-recurrence';
import { isVisitEvent, extractVisitId } from '@features/agenda/utils/visit-event';
import { CalendarService } from '@services/requests/housien-services/CalendarService';
import { agendaKeys } from '@features/agenda/hooks/agenda-keys';
import { useCalendars } from '@features/agenda/hooks/useCalendars';
import { useCalendarEvents } from '@features/agenda/hooks/useCalendarEvents';
import {
  useEventMutations,
  useCalendarMutations,
} from '@features/agenda/hooks/useAgendaMutations';
import { useAgendaState } from '@features/agenda/hooks/useAgendaState';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useGoogleConnectReturn } from '@features/agenda/hooks/useGoogleConnectReturn';
import type { EventRecurrenceScope } from '@services/requests/housien-models/CalendarEventInput';
import { isRecurring, mutationTargetId } from './recurrence-helpers';

/**
 * All state, queries, derived data, and CRUD handlers for the agenda page.
 * Extracted from {@link AgendaPage} so the page component stays a thin view.
 */
export function useAgenda() {
  const { t } = useTranslation('features/agenda');
  const { updateNavigationBar } = useLayout((state) => state);
  const { agency } = useAgency((state) => state);
  const agencyId = agency.id ?? '';
  const { currentUserRole, userEmail, isOperational } = useAgencyPermissions();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  useGoogleConnectReturn(agencyId);

  const calendarsQuery = useCalendars(agencyId);
  const calendars = useMemo(() => calendarsQuery.data ?? [], [calendarsQuery.data]);
  const isGoogleConnected = useMemo(
    () => calendars.some((c) => Boolean(c.sync?.google)),
    [calendars]
  );

  useEffect(() => {
    if (!agencyId || !isGoogleConnected) return;
    CalendarService.syncGoogle(agencyId)
      .then(() => queryClient.invalidateQueries({ queryKey: agendaKeys.all(agencyId) }))
      .catch(() => {
        // Silent: this is a background freshness pull, not a user-initiated
        // action — errors surface via the manual "Atualizar" button instead.
      });
  }, [agencyId, isGoogleConnected, queryClient]);

  // "Minhas agendas" means the agency's calendars plus the ones this user owns.
  // A privileged user also receives their teammates' personal calendars, but
  // those belong to the MEMBROS section — listing them here made the sidebar
  // show the same person twice and read as if their agenda were the user's own.
  const myCalendars = useMemo(
    () => calendars.filter(
      (c) => c.owner_type === 'agency' || c.owner_id === (userEmail ?? '')
    ),
    [calendars, userEmail]
  );

  const calendarIds = useMemo(
    () => myCalendars.map((c) => c.calendar_id).filter((id): id is string => Boolean(id)),
    [myCalendars]
  );

  const members = agency.members ?? [];
  const nonMeMembers = useMemo(
    () => otherMembers(members, userEmail ?? ''),
    [members, userEmail]
  );
  const memberEmails = useMemo(() => nonMeMembers.map((m) => m.email), [nonMeMembers]);

  const {
    view, setView, anchor, navigate, goToday, goToDay,
    activeCalendars, toggleCalendar, activeMembers, toggleMember,
  /*
   * The phone lands on the day screen, not the list.
   *
   * The day is what somebody opens the agenda to see — what is happening
   * now — and its week strip already shows where the rest of the week is
   * busy. The list is for reading several days in a row, which is the
   * second question, not the first.
   */
  } = useAgendaState(calendarIds, memberEmails, isMobile ? 'day' : 'week');

  const range = useMemo(() => viewRange(view, anchor), [view, anchor]);
  const eventsQuery = useCalendarEvents(agencyId, range.start, range.end);
  const { createEvent, updateEvent, deleteEvent } = useEventMutations(agencyId);
  const { createCalendar, updateCalendar, deleteCalendar } = useCalendarMutations(agencyId);

  const activeMembersList = useMemo(() => Array.from(activeMembers), [activeMembers]);
  const rangeKey = `${range.start.toISOString()}|${range.end.toISOString()}`;

  const memberQueries = useQueries({
    queries: activeMembersList.map((email) => ({
      queryKey: agendaKeys.events(agencyId, `${rangeKey}|organizer:${email}`),
      queryFn: async () => {
        const response = await CalendarService.listEvents(agencyId, undefined, email);
        return response.data ?? [];
      },
      enabled: Boolean(agencyId) && Boolean(email),
    })),
  });

  // Every member gets one colour, reserved against the calendars already on
  // screen so no two rows in the sidebar look alike.
  const roster = useMemo(
    () => [userEmail ?? '', ...members.map((m) => m.email)].filter(Boolean),
    [userEmail, members]
  );
  const colorOfMember = useMemo(
    () => memberColors(roster, calendars),
    [roster, calendars]
  );

  const memberEvents = useMemo<ExpandedEvent[]>(() => {
    const allRaw = memberQueries.flatMap((q) => q.data ?? []);
    return expandEvents(allRaw, range.start, range.end).map((ev) => ({
      ...ev,
      display_color: colorOfMember.get(ev.organizer_email ?? '') ?? undefined,
    }));
  }, [memberQueries, range.start, range.end, colorOfMember]);

  const [formOpen, setFormOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<ExpandedEvent | null>(null);
  const [formDefaultDate, setFormDefaultDate] = useState<Date>(anchor);
  const [pendingEdit, setPendingEdit] = useState<CalendarEventInput | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ExpandedEvent | null>(null);
  const [visitsVisible, setVisitsVisible] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!agencyId) return;
    const path = `/app/agencies/${agencyId}/agenda`;
    updateNavigationBar(<SideMenuLink label={t('menu.agenda')} linkTo={path} />, 2);
  }, [agencyId]);

  const visibleEvents = useMemo(() => {
    const keyOf = (e: ExpandedEvent) => `${e.event_id}:${e.occurrence_start}`;
    const byKey = new Map<string, ExpandedEvent>();
    for (const e of eventsQuery.events) {
      const shown = isVisitEvent(e) ? visitsVisible : activeCalendars.has(e.calendar_id);
      if (shown) byKey.set(keyOf(e), e);
    }
    // An event a member organizes on a calendar already on screen — the
    // institutional one, typically — arrives twice: once through its calendar
    // and once through the member filter. Keep the single occurrence and let
    // the member filter only colour it, instead of drawing a second chip for
    // the same event on the same slot.
    for (const e of memberEvents) {
      const key = keyOf(e);
      const already = byKey.get(key);
      byKey.set(key, already ? { ...already, display_color: e.display_color } : e);
    }
    return [...byKey.values()];
  }, [eventsQuery.events, activeCalendars, memberEvents, visitsVisible]);

  // The role has to allow creating events *and* leave at least one calendar to
  // put them on — a broker with no personal calendar has nowhere to write.
  const canCreateEvents =
    canManageEvent(
      currentUserRole,
      { organizer_email: userEmail ?? '' } as never,
      userEmail ?? ''
    ) && writableCalendars(calendars, userEmail ?? '', currentUserRole).length > 0;

  // "Novo evento" means now, not wherever the calendar happens to be parked:
  // browsing to another week to check something and then creating an event
  // used to silently pre-fill that week's day. Only a click on an actual grid
  // slot carries a date of its own.
  const openCreate = (slotDate?: Date) => {
    setSelected(null);
    setFormOpen(true);
    setFormDefaultDate(slotDate ?? new Date());
  };

  const openEvent = (event: ExpandedEvent) => {
    setSelected(event);
    setPreviewOpen(true);
  };

  const viewVisit = (event: ExpandedEvent) => {
    const visitId = extractVisitId(event);
    if (!visitId) return;
    // `/visits/:visit_id` is a dead-end redirect (`App.agencyRoutes.tsx`) that
    // discards the id — the real visit detail overlay is opened via `?visit=`
    // on the visits list route (see `VisitDetailOverlay`/`visit-status-lifecycle.spec.ts`).
    setPreviewOpen(false);
    nav(`/app/agencies/${agencyId}/visits?visit=${visitId}`);
  };

  const editFromPreview = (event: ExpandedEvent) => {
    setSelected(event);
    setPreviewOpen(false);
    setFormOpen(true);
  };

  // Being the organizer is not enough: the event also has to sit on a calendar
  // the user may write to, or the Edit/Delete buttons offer a write the API
  // refuses — a broker organizing an event on the institutional calendar, say.
  const selectedCalendar = selected
    ? calendars.find((c) => c.calendar_id === selected.calendar_id)
    : undefined;
  const canManageSelected = Boolean(
    selected &&
      selectedCalendar &&
      !isVisitEvent(selected) &&
      canManageEvent(currentUserRole, selected, userEmail ?? '') &&
      canWriteToCalendar(selectedCalendar, userEmail ?? '', currentUserRole)
  );

  const handleCreateEvent = async (input: CalendarEventInput) => {
    await createEvent.mutateAsync(input);
  };

  const handleUpdateEvent = async (_eventId: string, input: CalendarEventInput) => {
    if (selected && isRecurring(selected)) {
      setPendingEdit(input);
      return;
    }
    await updateEvent.mutateAsync({
      eventId: selected ? mutationTargetId(selected) : _eventId,
      body: input,
      scope: 'all',
    });
  };

  const requestDelete = (event: ExpandedEvent) => {
    setFormOpen(false);
    setPreviewOpen(false);
    if (isRecurring(event)) {
      setPendingDelete(event);
      return;
    }
    deleteEvent.mutate({ eventId: mutationTargetId(event), scope: 'all' });
  };

  const confirmEditScope = (scope: EventRecurrenceScope) => {
    if (!selected || !pendingEdit) return;
    updateEvent.mutate({
      eventId: mutationTargetId(selected),
      body: pendingEdit,
      scope,
      occurrence: scope === 'all' ? undefined : selected.occurrence_start,
    });
    setPendingEdit(null);
    setSelected(null);
  };

  const confirmDeleteScope = (scope: EventRecurrenceScope) => {
    if (!pendingDelete) return;
    deleteEvent.mutate({
      eventId: mutationTargetId(pendingDelete),
      scope,
      occurrence: scope === 'all' ? undefined : pendingDelete.occurrence_start,
    });
    setPendingDelete(null);
  };

  return {
    t, agency, agencyId, currentUserRole, userEmail, isOperational, isMobile,
    calendars, myCalendars, calendarsQuery, nonMeMembers, view, setView, anchor, navigate,
    goToday, goToDay, activeCalendars, toggleCalendar, activeMembers, toggleMember,
    eventsQuery, visibleEvents, canCreateEvents, canManageSelected,
    visitsVisible, toggleVisits: () => setVisitsVisible((v) => !v),
    formOpen, setFormOpen, previewOpen, setPreviewOpen, drawerOpen, setDrawerOpen,
    selected, formDefaultDate, pendingEdit, setPendingEdit, pendingDelete, setPendingDelete,
    openCreate, openEvent, viewVisit, editFromPreview, requestDelete,
    handleCreateEvent, handleUpdateEvent, confirmEditScope, confirmDeleteScope,
    createCalendar, updateCalendar, deleteCalendar,
  };
}
